import { useState } from "react"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router-dom"

import { ROOT_ROUTE } from "../../../constants/routes"
import { useApiMutation } from "../../../core/api/useApiMutation"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { authenticateByType } from "../../../modules/login/loginApi"
import { login, logout } from "../../../modules/login/loginSlice"
import { AuthenticationTokenType, isAuthenticationComplete } from "../../../modules/login/loginTypes"
import { useAppDispatch } from "../../../modules/store/storeHooks"
import { getUserDetails } from "../../../modules/users/userApi"

import { ConfirmModal } from "./ConfirmModal"

export type ImpersonationTarget = Readonly<{
    userId: number
    displayName: string
    /** Fetched on demand when missing (the administrators list does not carry it). */
    username?: string
}>

type ImpersonationConfirmDialogProps = Readonly<{
    target: ImpersonationTarget | null
    onClose: () => void
}>

/**
 * Confirmation shown before a supporter signs in as the picked user. On confirmation the
 * session is swapped the way the login flow does it, with logout first so every slice
 * listening for it resets. The switch is deliberately one-way: the way back is logging out.
 */
export function ImpersonationConfirmDialog({ target, onClose }: ImpersonationConfirmDialogProps) {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()

    const [isImpersonating, setImpersonating] = useState(false)

    const { mutateAsync: callGetUserDetails } = useApiMutation(getUserDetails, {})
    const { mutateAsync: callAuthenticateByType } = useApiMutation(authenticateByType, {})

    const handleConfirm = async () => {
        if (!target) return

        setImpersonating(true)
        try {
            const username = target.username ?? (await callGetUserDetails({ userId: target.userId })).user.username
            const response = await callAuthenticateByType({
                type: AuthenticationTokenType.IMPERSONATION,
                value: username,
            })

            if (!isAuthenticationComplete(response)) {
                enqueueSnackbar({
                    variant: "error",
                    message: l10n.getString("auth-flow-authentication-unexpected-server-error", {
                        action: "authenticationResult" in response ? response.authenticationResult.action : "NONE",
                    }),
                })
                return
            }

            if (response.authenticationResult.message) {
                enqueueSnackbar({ variant: "success", message: response.authenticationResult.message })
            }

            // Both dispatches land in one batch, so the auth guard never sees the gap
            dispatch(logout())
            dispatch(
                login({
                    authToken: response.authentication.token,
                    user: response.user,
                }),
            )
            navigate(ROOT_ROUTE)
        } catch {
            // The API layer already showed the error snackbar; the dialog stays open
        } finally {
            setImpersonating(false)
        }
    }

    return (
        <LocalizedStrict
            id="impersonation-confirm-dialog"
            attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
            vars={{ name: target?.displayName ?? "" }}
        >
            <ConfirmModal
                open={Boolean(target)}
                onClose={onClose}
                title="Impersonate this user?"
                message="You will see the portal exactly as this user does. Everything you do is recorded in their history under your own name."
                buttonLabelCancel="Cancel"
                buttonLabelSubmit="Start session"
                buttonColorSubmit="black"
                buttonSubmitLoading={isImpersonating}
                autoCloseAfterSubmit={false}
                handleClick={handleConfirm}
            />
        </LocalizedStrict>
    )
}
